import React, { useState } from 'react';
import { SessionData } from '../../../shared/services/SessionsService';
import { sessionsService, StudentProfile } from '../../../shared/services/SessionsService';
import '../styles/Sessions.css';

interface SessionsTableProps {
  sessions: SessionData[];
  loading: boolean;
  onViewFeedback: (sessionId: string) => void;
  onDeleteSession: (sessionId: string) => void;
}

type SortField = 'date' | 'squad' | 'curriculum' | 'teacher' | 'attendance' | 'completion' | 'duration' | 'rating' | 'studentRating';
type SortDirection = 'asc' | 'desc';

const SessionsTable: React.FC<SessionsTableProps> = ({ sessions, loading, onViewFeedback, onDeleteSession }) => {
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<SessionData | null>(null);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const sortedSessions = React.useMemo(() => {
    if (!sessions.length) return [];

    return [...sessions].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'date':
          comparison = a.startTime.toMillis() - b.startTime.toMillis();
          break;
        case 'squad':
          comparison = a.squadTitle.localeCompare(b.squadTitle);
          break;
        case 'curriculum':
          comparison = (a.curriculumId || '').localeCompare(b.curriculumId || '');
          break;
        case 'teacher':
          comparison = a.teacherName.localeCompare(b.teacherName);
          break;
        case 'attendance':
          comparison = a.attendanceRate - b.attendanceRate;
          break;
        case 'completion':
          comparison = a.completionPercentage - b.completionPercentage;
          break;
        case 'duration':
          comparison = a.duration - b.duration;
          break;
        case 'rating':
          comparison = (a.teacherRating || 0) - (b.teacherRating || 0);
          break;
        case 'studentRating':
          comparison = (a.studentRating5 || 0) - (b.studentRating5 || 0);
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [sessions, sortField, sortDirection]);

  const formatTime = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };


  const getChicagoHM = (timestamp: any): { hours: number; minutes: number } => {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(date);
    const hours = Number(parts.find(p => p.type === 'hour')?.value || '0');
    const minutes = Number(parts.find(p => p.type === 'minute')?.value || '0');
    return { hours, minutes };
  };

  const computeMinutesEarly = (session: any): number | null => {
    if (!session.scheduledStartTime || !session.startTime) return null;
    const { hours: actualH, minutes: actualM } = getChicagoHM(session.startTime);
    const [schedHStr, schedMStr] = String(session.scheduledStartTime).split(':');
    const schedH = Number(schedHStr);
    const schedM = Number(schedMStr);
    if (Number.isNaN(schedH) || Number.isNaN(schedM)) return null;
    const actualMinutes = actualH * 60 + actualM;
    const scheduledMinutes = schedH * 60 + schedM;
    // Positive => started early; Negative => late; 0 => on time
    return scheduledMinutes - actualMinutes;
  };

  const getPunctualityColor = (minutesEarly: number | null) => {
    if (minutesEarly == null) return '#6b7280';
    if (minutesEarly >= 15) return '#10b981';
    if (minutesEarly > 0) return '#f59e0b';
    return '#ef4444';
  };

  const to12Hour = (hhmm?: string) => {
    if (!hhmm) return '';
    const [hStr, mStr] = hhmm.split(':');
    let h = Number(hStr);
    const m = mStr.padStart(2, '0');
    if (Number.isNaN(h)) return hhmm;
    const h12 = ((h % 12) || 12).toString();
    return `${h12}:${m}`;
  };

  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [attendanceSession, setAttendanceSession] = useState<SessionData | null>(null);
  const [studentNames, setStudentNames] = useState<Record<string, string>>({});
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [studentProfiles, setStudentProfiles] = useState<Record<string, StudentProfile>>({});

  // Student Rating modal state
  const [showStudentRatings, setShowStudentRatings] = useState(false);
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [ratingsSession, setRatingsSession] = useState<SessionData | null>(null);
  const [studentRatings, setStudentRatings] = useState<Array<{ studentId: string; name: string; value: number }>>([]);

  const openAttendanceModal = async (session: SessionData) => {
    setAttendanceSession(session);
    setShowAttendanceModal(true);
    const ids = Object.keys(session.attendanceData || {});
    if (ids.length) {
      setAttendanceLoading(true);
      const [names, profiles] = await Promise.all([
        sessionsService.fetchStudentNamesByIds(ids),
        sessionsService.fetchStudentProfilesByIds(ids)
      ]);
      setStudentNames(names);
      setStudentProfiles(profiles);
      setAttendanceLoading(false);
    } else {
      setStudentNames({});
      setStudentProfiles({});
    }
  };


  const getAttendanceColor = (rate: number) => {
    if (rate >= 85) return '#10b981'; // green
    if (rate >= 70) return '#f59e0b'; // amber
    return '#ef4444'; // red
  };

  const getCompletionColor = (percentage: number) => {
    if (percentage >= 90) return '#10b981'; // green
    if (percentage >= 70) return '#f59e0b'; // amber
    if (percentage >= 50) return '#f97316'; // orange
    return '#ef4444'; // red
  };

  const getLocationIcon = (status: string) => {
    switch (status) {
      case 'on-site': return '✅';
      case 'near-site': return '⚠️';
      case 'off-site': return '❌';
      default: return '❓';
    }
  };

  const getLocationColor = (status: string) => {
    switch (status) {
      case 'on-site': return '#10b981';
      case 'near-site': return '#f59e0b';
      case 'off-site': return '#ef4444';
      default: return '#6b7280';
    }
  };


  const getRatingClass = (rating?: number) => {
    if (rating == null) return 'rating-na';
    if (rating >= 9) return 'rating-excellent';
    if (rating >= 7) return 'rating-good';
    return 'rating-poor';
  };

  const formatRating10 = (rating?: number) => {
    if (rating == null) return 'N/A';
    const integer = Math.round(rating);
    const isWhole = Math.abs(rating - integer) < 1e-9;
    return `${isWhole ? integer : rating.toFixed(1)}/10`;
  };

  if (loading) {
    return (
      <div className="sessions-table-container">
        <div className="loading-skeleton">
          <div className="skeleton-header"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="skeleton-row">
              {[...Array(8)].map((_, j) => (
                <div key={j} className="skeleton-cell"></div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!sessions.length) {
    return (
      <div className="sessions-table-container">
        <div className="empty-state">
          <h3>No sessions found</h3>
          <p>Try adjusting your filters to see more results.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sessions-table-container">
      <div className="table-wrapper">
        <table className="sessions-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('date')} className="sortable">
                Date/Time {getSortIcon('date')}
              </th>
              <th onClick={() => handleSort('squad')} className="sortable">
                Squad {getSortIcon('squad')}
              </th>
              <th onClick={() => handleSort('curriculum')} className="sortable">
                Curriculum {getSortIcon('curriculum')}
              </th>
              <th onClick={() => handleSort('teacher')} className="sortable">
                Teacher {getSortIcon('teacher')}
              </th>
              <th>Location</th>
              <th>Timing</th>
              <th onClick={() => handleSort('attendance')} className="sortable">
                Attendance {getSortIcon('attendance')}
              </th>
              <th onClick={() => handleSort('completion')} className="sortable">
                Completion {getSortIcon('completion')}
              </th>
              <th onClick={() => handleSort('rating')} className="sortable">
                Teacher Rating {getSortIcon('rating')}
              </th>
              <th onClick={() => handleSort('studentRating')} className="sortable">
                Student Rating {getSortIcon('studentRating')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedSessions.map((session) => (
              <tr 
                key={session.id} 
                className={session.isLive ? 'live-session' : ''}
                style={session.isLive ? { 
                  outline: '2px solid #10b981', 
                  outlineOffset: '-2px',
                  backgroundColor: '#f0fdf4'
                } : {}}
              >
                <td>
                  <div className="date-cell">
                    <div className="start-time">
                      {session.isLive && <span className="live-indicator">🟢 LIVE</span>}
                      {formatTime(session.startTime)}
                    </div>
                    {session.endTime && (
                      <div className="end-time">End: {formatTime(session.endTime)}</div>
                    )}
                  </div>
                </td>
                <td>
                  <div className="squad-cell">
                    <div className="squad-name">{session.squadTitle}</div>
                    <div className="level-info">Level {session.levelId}</div>
                  </div>
                </td>
                <td>{session.curriculumId || 'music'}</td>
                <td>{session.teacherName}</td>
                <td>
                  <div 
                    className="location-cell"
                    style={{ color: getLocationColor(session.locationStatus) }}
                  >
                    <span className="location-icon">
                      {getLocationIcon(session.locationStatus)}
                    </span>
                    <span className="location-status">
                      {session.locationStatus.replace('-', ' ')}
                    </span>
                    {session.distanceFromExpected !== undefined && (
                      <div className="distance">
                        {session.distanceFromExpected < 1000 
                          ? `${Math.round(session.distanceFromExpected)}m`
                          : `${(session.distanceFromExpected / 1000).toFixed(1)}km`
                        }
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  <div className="timing-cell">
                    {session.scheduledStartTime && (
                      <div className="scheduled-time">
                        Scheduled: {to12Hour(session.scheduledStartTime)}
                      </div>
                    )}
                    {(() => {
                      const mEarly = computeMinutesEarly(session);
                      if (mEarly == null) return null;
                      const label = mEarly > 0 ? `${mEarly}m early` : mEarly < 0 ? `${-mEarly}m late` : 'On time';
                      return (
                        <div className="punctuality" style={{ color: getPunctualityColor(mEarly) }}>
                          {label}
                        </div>
                      );
                    })()}
                  </div>
                </td>
                <td onClick={() => openAttendanceModal(session)} style={{ cursor: 'pointer' }}>
                  <div 
                    className="attendance-cell"
                    style={{ color: getAttendanceColor(session.attendanceRate) }}
                    title="View attendance details"
                  >
                    <div className="attendance-numbers">
                      {session.attendanceCount}/{session.totalStudents}
                    </div>
                    <div className="attendance-percentage">
                      ({session.attendanceRate}%)
                    </div>
                  </div>
                </td>
                <td>
                  <div className="completion-cell">
                    <div 
                      className="completion-bar"
                      style={{ 
                        background: `linear-gradient(90deg, ${getCompletionColor(session.completionPercentage)} ${session.completionPercentage}%, #e5e7eb ${session.completionPercentage}%)` 
                      }}
                    >
                      <span className="completion-text">
                        {session.completionPercentage}%
                      </span>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="rating-cell">
                    <div className={`rating-number rating-large ${getRatingClass(session.teacherRating)}`}>
                      {formatRating10(session.teacherRating)}
                    </div>
                  </div>
                </td>
                <td onClick={async () => {
                  setRatingsSession(session);
                  setShowStudentRatings(true);
                  setRatingsLoading(true);
                  try {
                    // Try id first, then altSessionId if available
                    let raw = await sessionsService.fetchStudentEmojiRatings(session.id);
                    if ((!raw || raw.length === 0) && session.altSessionId) {
                      raw = await sessionsService.fetchStudentEmojiRatings(session.altSessionId);
                    }
                    const ids = raw.map(r => r.studentId);
                    const names = await sessionsService.fetchStudentNamesByIds(ids);
                    setStudentRatings(raw.map(r => ({ studentId: r.studentId, name: names[r.studentId] || r.studentId, value: r.value })));
                  } finally {
                    setRatingsLoading(false);
                  }
                }} style={{ cursor: 'pointer' }} title={session.studentRating5 != null ? 'View individual ratings' : 'No student feedback yet'}>
                  <div className="rating-cell">
                    <div className={`rating-number rating-large ${getRatingClass(session.studentRating5 != null ? (session.studentRating5 * 2) : undefined)}`}>
                      {session.studentRating5 != null ? `${session.studentRating5}/5` : 'N/A'}
                    </div>
                  </div>
                </td>
                <td>
                  <div className="actions-cell">
                    <button
                      className="feedback-button"
                      onClick={() => onViewFeedback(session.id)}
                      disabled={!session.feedbackFormId}
                      title={session.feedbackFormId ? 'View feedback form' : 'No feedback available'}
                    >
                      📋
                    </button>
                    <button
                      className="delete-session-button"
                      onClick={() => {
                        setSessionToDelete(session);
                        setShowDeleteModal(true);
                      }}
                      title="Delete session"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showDeleteModal && sessionToDelete && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowDeleteModal(false); }}>
          <div className="confirm-modal">
            <div className="modal-header">
              <h3>Confirm Session Deletion</h3>
              <button className="close-button" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-content">
              <div className="confirm-body">
                <p className="confirm-intro">You're about to permanently delete this session. Please verify the details:</p>
                <ul className="confirm-details">
                  <li>
                    <strong>Squad</strong>
                    <span className="confirm-value">{sessionToDelete.squadTitle || 'Unknown Squad'}</span>
                  </li>
                  <li>
                    <strong>Teacher</strong>
                    <span className="confirm-value">{sessionToDelete.teacherName || 'Unknown Teacher'}</span>
                  </li>
                  <li>
                    <strong>Date/Time</strong>
                    <span className="confirm-value">{sessionToDelete.startTime ? formatTime(sessionToDelete.startTime) : 'N/A'}{sessionToDelete.endTime ? ` – ${formatTime(sessionToDelete.endTime)}` : ''}</span>
                  </li>
                </ul>
                <p className="confirm-warning">This action cannot be undone.</p>
              </div>
            </div>
            <div className="modal-footer">
              <div className="confirm-actions">
                <button className="confirm-cancel-btn" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button
                  className="confirm-delete-btn"
                  onClick={() => {
                    onDeleteSession(sessionToDelete.id);
                    setShowDeleteModal(false);
                    setSessionToDelete(null);
                  }}
                >
                  Delete Session
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAttendanceModal && attendanceSession && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowAttendanceModal(false); }}>
          <div className="attendance-modal">
            <div className="modal-header">
              <h3>Attendance Details</h3>
              <button className="close-button" onClick={() => setShowAttendanceModal(false)}>×</button>
            </div>
            <div className="modal-content">
              {attendanceLoading ? (
                <div className="loading-state"><div className="loading-spinner"></div><p>Loading students…</p></div>
              ) : (
                <div className="attendance-grid">
                  <div className="attendance-column present">
                    <div className="attendance-header">
                      <h4>Present</h4>
                      <span className="attendance-count">{Object.entries(attendanceSession.attendanceData || {}).filter(([, v]) => v).length}</span>
                    </div>
                    <div className="attendance-chips">
                      {Object.entries(attendanceSession.attendanceData || {})
                        .filter(([, v]) => v)
                        .map(([id]) => {
                          const profile = studentProfiles[id];
                          const name = studentNames[id] || profile?.displayName || id;
                          const initials = name.split(' ').map(p => p[0]).join('').slice(0,2).toUpperCase();
                          return (
                            <div key={id} className="student-chip present">
                              {profile?.photoURL ? (
                                <img src={profile.photoURL} alt={name} className="chip-avatar" />
                              ) : (
                                <div className="chip-initials" aria-hidden>{initials}</div>
                              )}
                              <span className="chip-name">{name}</span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                  <div className="attendance-column absent">
                    <div className="attendance-header">
                      <h4>Absent</h4>
                      <span className="attendance-count">{Object.entries(attendanceSession.attendanceData || {}).filter(([, v]) => !v).length}</span>
                    </div>
                    <div className="attendance-chips">
                      {Object.entries(attendanceSession.attendanceData || {})
                        .filter(([, v]) => !v)
                        .map(([id]) => {
                          const profile = studentProfiles[id];
                          const name = studentNames[id] || profile?.displayName || id;
                          const initials = name.split(' ').map(p => p[0]).join('').slice(0,2).toUpperCase();
                          return (
                            <div key={id} className="student-chip absent">
                              {profile?.photoURL ? (
                                <img src={profile.photoURL} alt={name} className="chip-avatar" />
                              ) : (
                                <div className="chip-initials" aria-hidden>{initials}</div>
                              )}
                              <span className="chip-name">{name}</span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="close-modal-button" onClick={() => setShowAttendanceModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showStudentRatings && ratingsSession && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowStudentRatings(false); }}>
          <div className="ratings-modal">
            <div className="modal-header">
              <h3>Student Ratings</h3>
              <button className="close-button" onClick={() => setShowStudentRatings(false)}>×</button>
            </div>
            <div className="modal-content">
              {ratingsLoading ? (
                <div className="loading-state"><div className="loading-spinner"></div><p>Loading ratings…</p></div>
              ) : (
                <table className="student-ratings-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentRatings.length === 0 ? (
                      <tr><td colSpan={2} style={{ textAlign: 'center', color: '#6b7280' }}>No student ratings submitted</td></tr>
                    ) : (
                      studentRatings
                        .sort((a,b) => a.name.localeCompare(b.name))
                        .map(r => (
                          <tr key={r.studentId}>
                            <td>{r.name}</td>
                            <td>{r.value}/5</td>
                          </tr>
                        ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionsTable;